import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@app/auth-client";
import { createApiClient, type ApiClient } from "./api";
import type { MeResponse, Membership } from "./types";

export type PortalContextValue = {
  api: ApiClient;
  me: MeResponse | null;
  loading: boolean;
  error: string | null;
  isBkdpAdmin: boolean;
  memberships: Membership[];
  refresh: () => Promise<void>;
};

const PortalContext = createContext<PortalContextValue | undefined>(undefined);

type Result = { token: string; me: MeResponse | null; error: string | null };

/**
 * Fetches /me once the user is authenticated and exposes the API client.
 * Must sit inside AuthProvider.
 *
 * `loading` stays true from the moment a token exists until /me has answered
 * for that token, so consumers never see a "signed in but no memberships"
 * frame while the request is in flight.
 */
export const PortalProvider: React.FC<React.PropsWithChildren<{ apiBaseUrl: string }>> = ({
  apiBaseUrl,
  children
}) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const token = isAuthenticated ? user?.id_token : undefined;

  const api = useMemo(
    () => createApiClient({ baseUrl: apiBaseUrl, getToken: () => token }),
    [apiBaseUrl, token]
  );

  const [result, setResult] = useState<Result | null>(null);

  const refresh = useCallback(async () => {
    if (!token) {
      return;
    }
    const forToken = token;
    try {
      const me = await api.request<MeResponse>("/me");
      setResult({ token: forToken, me, error: null });
    } catch (err) {
      setResult({
        token: forToken,
        me: null,
        error: err instanceof Error ? err.message : "Could not load your profile"
      });
    }
  }, [api, token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const fresh = result !== null && result.token === token;
  const me = fresh ? result.me : null;
  const error = fresh ? result.error : null;
  const loading = authLoading || (Boolean(token) && !fresh);

  const value = useMemo<PortalContextValue>(
    () => ({
      api,
      me,
      loading,
      error,
      isBkdpAdmin: Boolean(me?.isBkdpAdmin),
      memberships: me?.memberships ?? [],
      refresh
    }),
    [api, me, loading, error, refresh]
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
};

export const usePortal = (): PortalContextValue => {
  const ctx = useContext(PortalContext);
  if (!ctx) {
    throw new Error("usePortal must be used within PortalProvider");
  }
  return ctx;
};
